import { useMemo, type ReactNode } from "react";
import { formatCustomerDisplayName } from "@/domain/customers/formatCustomer";
import {
  formatAmountChip,
  formatDateRangeChip,
  formatDueDateChip,
  resolveInvoiceSearchCustomersHint,
  validateInvoiceFilterValues,
  type InvoiceFilterValues,
} from "@/domain/invoices/invoiceFilterState";
import { FilterValidationAlert } from "@/features/invoices/components/FilterValidationAlert";
import { INVOICE_STATUS_MULTI_OPTIONS } from "@/domain/invoices/invoiceStatus";
import { CREDIT_NOTE_STATUS_OPTIONS } from "@/domain/creditNotes/creditNoteStatus";
import { AmountRangeFilter } from "@/features/invoices/components/AmountRangeFilter";
import { CustomerMultiSelect } from "@/features/invoices/components/CustomerMultiSelect";
import { DateRangeFilter } from "@/features/invoices/components/DateRangeFilter";
import { MultiSelect } from "@/features/invoices/components/MultiSelect";
import type { CustomerRow } from "@/types/api";
import { cn } from "@/utils/cn";

interface InvoiceFiltersPanelProps {
  draft: InvoiceFilterValues;
  applied: InvoiceFilterValues;
  isDirty: boolean;
  customers: CustomerRow[];
  customersLoading?: boolean;
  customersError?: string | null;
  onDraftChange: (next: InvoiceFilterValues) => void;
  onSearch: () => void;
  /** Disable Search while the list is loading after a filter search. */
  searchLoading?: boolean;
  onReloadCustomers?: () => void;
  hideStatus?: boolean;
  showDueDate?: boolean;
  statusHint?: string;
  dateFilterHint?: string;
  layoutVariant?: "default" | "credit-notes";
  createdDateLabel?: string;
  statusLabel?: string;
  statusOptions?: { value: string; label: string }[];
  /** Tighter padding on download list pages. */
  compact?: boolean;
}

function LastSearchChip({ children }: { children: ReactNode }) {
  return (
    <span className="rounded-full border border-[var(--border)] bg-[var(--surface)] px-2 py-0.5 text-xs font-semibold">
      {children}
    </span>
  );
}

function AppliedFilterChips({
  applied,
  hideStatus,
  layoutVariant,
  showDueDate,
}: {
  applied: InvoiceFilterValues;
  hideStatus?: boolean;
  layoutVariant: "default" | "credit-notes";
  showDueDate: boolean;
}) {
  const createdChipLabel =
    layoutVariant === "credit-notes" ? "CN created" : "Created";
  const appliedAmountChip = formatAmountChip(applied.amountMin, applied.amountMax);
  const appliedDueChip = formatDueDateChip(
    applied.dueDateFrom,
    applied.dueDateTo,
  );

  return (
    <>
      <LastSearchChip>
        {applied.customerIds.length === 0 ? (
          <>
            <strong className="text-[var(--accent)]">All</strong> customers
          </>
        ) : (
          <>
            <strong className="text-[var(--accent)]">
              {applied.customerIds.length}
            </strong>{" "}
            customer{applied.customerIds.length === 1 ? "" : "s"}
          </>
        )}
      </LastSearchChip>
      {!hideStatus ? (
        <LastSearchChip>
          {applied.statuses.length === 0 ? (
            <>
              <strong className="text-[var(--accent)]">All</strong> statuses
            </>
          ) : (
            <>
              <strong className="text-[var(--accent)]">
                {applied.statuses.length}
              </strong>{" "}
              {applied.statuses.length === 1 ? "status" : "statuses"}
            </>
          )}
        </LastSearchChip>
      ) : null}
      {appliedAmountChip ? (
        <LastSearchChip>{appliedAmountChip}</LastSearchChip>
      ) : null}
      <LastSearchChip>
        {formatDateRangeChip(
          applied.dateFrom,
          applied.dateTo,
          createdChipLabel,
        )}
      </LastSearchChip>
      {showDueDate && appliedDueChip ? (
        <LastSearchChip>{appliedDueChip}</LastSearchChip>
      ) : null}
    </>
  );
}

export function InvoiceFiltersPanel({
  draft,
  applied,
  isDirty,
  customers,
  customersLoading,
  customersError,
  onDraftChange,
  onSearch,
  searchLoading = false,
  onReloadCustomers,
  hideStatus,
  showDueDate = true,
  statusHint,
  dateFilterHint,
  layoutVariant = "default",
  createdDateLabel = "Invoice created",
  statusLabel = "Status",
  statusOptions: statusOptionsProp,
  compact = false,
}: InvoiceFiltersPanelProps) {
  const customerOptions = useMemo(
    () =>
      customers.map((c) => ({
        value: c.id,
        title: formatCustomerDisplayName(c),
        subtitle: c.email ? `${c.email} · ${c.id}` : c.id,
      })),
    [customers],
  );

  const statusOptions = useMemo(
    () =>
      (statusOptionsProp ??
        (layoutVariant === "credit-notes"
          ? CREDIT_NOTE_STATUS_OPTIONS.map((o) => ({
              value: o.value,
              label: o.label,
            }))
          : INVOICE_STATUS_MULTI_OPTIONS.map((o) => ({
              value: o.value,
              label: o.label,
            })))),
    [layoutVariant, statusOptionsProp],
  );

  const patch = (partial: Partial<InvoiceFilterValues>) =>
    onDraftChange({ ...draft, ...partial });

  const filterIssues = useMemo(
    () =>
      validateInvoiceFilterValues(draft, {
        createdDateLabel,
        includeDueDate: showDueDate,
      }),
    [draft, createdDateLabel, showDueDate],
  );

  const canSearch = filterIssues.length === 0;
  const customersHint = resolveInvoiceSearchCustomersHint(draft.customerIds);

  const handleSearch = () => {
    if (!canSearch || searchLoading) return;
    onSearch();
  };

  return (
    <div className={cn("filters-panel", compact && "filters-panel--compact")}>
      <div
        className={cn(
          "filters-panel-head flex flex-wrap items-start justify-between gap-2",
          compact ? "mb-2" : "mb-3",
        )}
      >
        <div>
          <h3 className="text-sm font-semibold">
            Filters
            {statusHint ? (
              <span className="ml-1.5 text-xs font-medium text-[var(--muted)]">
                · {statusHint}
              </span>
            ) : null}
          </h3>
        </div>
        <div
          className="flex flex-wrap items-center justify-end gap-1.5"
          aria-label="Last search"
        >
          <span
            className={cn(
              "rounded-full border border-amber-200 bg-amber-50 px-2 py-0.5 text-xs font-semibold text-amber-800",
              !isDirty && "invisible",
            )}
            aria-hidden={!isDirty}
          >
            Unsaved changes — Search to apply
          </span>
          <span className="text-[0.68rem] font-bold uppercase tracking-wide text-[var(--muted)]">
            Last search
          </span>
          <AppliedFilterChips
            applied={applied}
            hideStatus={hideStatus}
            layoutVariant={layoutVariant}
            showDueDate={showDueDate}
          />
        </div>
      </div>

      {dateFilterHint ? (
        <p className="mb-2 text-xs text-[var(--muted)]">{dateFilterHint}</p>
      ) : null}

      {customersError ? (
        <p className="mb-2 text-sm text-[var(--muted)]">
          {customersError}
          {onReloadCustomers ? (
            <>
              {" "}
              <button
                type="button"
                className="font-semibold underline"
                onClick={() => onReloadCustomers()}
              >
                Retry
              </button>
            </>
          ) : null}
        </p>
      ) : null}

      <div
        className={cn(
          "filter-row-download-filters filter-row-invoices overflow-visible",
          compact && "filter-row-download-filters--compact",
          hideStatus && "filter-row-invoices--no-status",
          layoutVariant === "credit-notes" && "filter-row-invoices--credit-notes",
        )}
        lang="en"
        onKeyDown={(e) => {
          if (e.key !== "Enter" || e.defaultPrevented) return;
          const tag = (e.target as HTMLElement).tagName;
          if (tag === "INPUT" || tag === "TEXTAREA" || tag === "SELECT") return;
          e.preventDefault();
          handleSearch();
        }}
      >
        <div className="filter-slot-customers">
          <CustomerMultiSelect
            label="Customers"
            options={customerOptions}
            selected={draft.customerIds}
            onChange={(customerIds) => patch({ customerIds })}
            allLabel="All customers"
            searchPlaceholder="Search by email or name…"
            disabled={customersLoading}
            loading={customersLoading}
          />
        </div>

        {hideStatus ? null : (
          <div className="filter-slot-status">
            <MultiSelect
              label={statusLabel}
              options={statusOptions}
              selected={draft.statuses}
              onChange={(statuses) => patch({ statuses })}
              allLabel="All statuses"
            />
          </div>
        )}

        <AmountRangeFilter
          amountMin={draft.amountMin}
          amountMax={draft.amountMax}
          onChange={(partial) => patch(partial)}
        />

        <div className="filter-slot-created">
          <DateRangeFilter
            label={createdDateLabel}
            dateFrom={draft.dateFrom}
            dateTo={draft.dateTo}
            disableFuture
            onChange={(partial) =>
              patch({
                dateFrom: partial.dateFrom ?? draft.dateFrom,
                dateTo: partial.dateTo ?? draft.dateTo,
              })
            }
          />
        </div>

        {showDueDate ? (
          <div className="filter-slot-due">
            <DateRangeFilter
              label="Due date"
              dateFrom={draft.dueDateFrom}
              dateTo={draft.dueDateTo}
              onChange={(partial) =>
                patch({
                  dueDateFrom: partial.dateFrom ?? draft.dueDateFrom,
                  dueDateTo: partial.dateTo ?? draft.dueDateTo,
                })
              }
            />
          </div>
        ) : null}

        <div className="filter-actions-col pb-0.5">
          <span
            className="filter-actions-label mb-1 block text-[0.72rem] font-semibold text-[var(--muted)] invisible"
            aria-hidden
          >
            Search
          </span>
          <button
            type="button"
            className="btn-main filter-search-btn disabled:cursor-not-allowed disabled:opacity-50"
            onClick={handleSearch}
            disabled={!canSearch || searchLoading}
            aria-busy={searchLoading || undefined}
            aria-label={searchLoading ? "Searching" : "Search"}
          >
            Search
          </button>
        </div>
      </div>

      {customersHint ? (
        <p className="filter-customers-hint" role="status">
          {customersHint}
        </p>
      ) : null}

      <FilterValidationAlert
        messages={filterIssues}
        title="Fix filters before searching"
      />
    </div>
  );
}
